import { getAccessToken, getRefreshToken, readAccessTokenClaims, storeJarvisTokens } from '@utils/access-token';
import { clearJarvisSession } from '@utils/auth-session';
import { syncTabCachesWithToken } from '@utils/tab-session-cache';
import {
  IMPERSONATED_USER_INFO_KEY,
  IMPERSONATION_CHANGED_EVENT,
  IMPERSONATION_SESSION_KEY,
  ORIGINAL_AUTH_DATA_KEY,
  ORIGINAL_TOKENS_KEY,
  clearImpersonationState,
} from '@utils/impersonation-state';

export { clearImpersonationState };

export interface ImpersonationSession {
  id: number;
  impersonatorId: number;
  targetUserId: number;
  startedAt: string;
  reason?: string;
}

export interface StartImpersonationResponse {
  success: boolean;
  data: {
    id: number;
    email: string;
    name?: string;
    accessToken: string;
    refreshToken: string;
    ppjkNpwp?: string;
    /** impersonation_sessions.id (backend#1520). */
    impersonationSessionId?: number;
    [key: string]: any;
  };
  message: string;
}

export interface User {
  id: number;
  email: string;
  name?: string;
}

/** 'restored': back on the staff account. 'signedOut': could not restore it, sent to /auth. */
export type StopImpersonationResult = 'restored' | 'signedOut';

/** 'switched': now impersonating the new target. 'signedOut': no staff session to switch from, sent to /auth. */
export type SwitchImpersonationResult = 'switched' | 'signedOut';

type StaffTokens = { accessToken: string; refreshToken: string; nomorPpjk?: string | null };

const ALREADY_IMPERSONATING = 'ALREADY_IMPERSONATING';

// Stop could not put the staff session back: end everything and sign in again.
const signOutAfterFailedRestore = (): 'signedOut' => {
  clearJarvisSession();
  clearImpersonationState();
  window.location.href = '/auth';
  return 'signedOut';
};

/** The staff session saved when the first impersonation started, or null. */
const readStaffSession = (): { tokens: StaffTokens; authData: unknown } | null => {
  try {
    const tokens = JSON.parse(localStorage.getItem(ORIGINAL_TOKENS_KEY) ?? 'null') as StaffTokens | null;
    const authData = JSON.parse(localStorage.getItem(ORIGINAL_AUTH_DATA_KEY) ?? 'null');
    if (!tokens?.accessToken || !tokens?.refreshToken || !authData) return null;
    return { tokens, authData };
  } catch {
    return null;
  }
};

class StartImpersonationError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'StartImpersonationError';
  }
}

/** POST /impersonation/start with the given (staff) token. Throws StartImpersonationError. */
const requestStart = async (
  accessToken: string,
  targetUserId: number,
  reason?: string
): Promise<StartImpersonationResponse> => {
  const response = await fetch('/api/v1/auth/impersonation/start', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ targetUserId, reason: reason || '' }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to start impersonation' }));
    if (response.status === 409 && error?.error?.code === ALREADY_IMPERSONATING) {
      throw new StartImpersonationError(
        error.error.message || 'Stop the current impersonation before starting another.',
        409
      );
    }
    throw new StartImpersonationError(error.message || 'Failed to start impersonation', response.status);
  }
  return response.json();
};

/** Makes the started impersonation the browser's session. */
const applyStarted = (data: StartImpersonationResponse, reason?: string) => {
  // IMPORTANT: Update tokens in BOTH cookies AND localStorage
  // This ensures axios interceptor picks up the impersonated user's token
  storeJarvisTokens(data.data.accessToken, data.data.refreshToken);
  localStorage.setItem('authData', JSON.stringify(data.data));

  // Update PPJK NPWP for impersonated user (only if not empty)
  if (data.data.ppjkNpwp && data.data.ppjkNpwp.trim() !== '') {
    localStorage.setItem('nomor_ppjk', data.data.ppjkNpwp);
  } else {
    // Remove nomor_ppjk if impersonated user doesn't have one
    localStorage.removeItem('nomor_ppjk');
  }

  // Mark as impersonating with session info
  const impersonationSession: ImpersonationSession = {
    id: data.data.impersonationSessionId ?? 0,
    impersonatorId: 0, // Will be set by JWT claims
    targetUserId: data.data.id,
    startedAt: new Date().toISOString(),
    reason: reason || '',
  };
  localStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(impersonationSession));
  localStorage.setItem(IMPERSONATED_USER_INFO_KEY, JSON.stringify(data.data));

  // The tab's draft caches were read for the previous account.
  syncTabCachesWithToken();
};

/**
 * POST /impersonation/stop with the impersonation token. Best effort: failures
 * are logged. `keepalive` lets the request finish across the reload that
 * follows a switch.
 */
const postStop = async (impersonationToken: string, sessionId: number, { keepalive = false } = {}) => {
  try {
    const response = await fetch('/api/v1/auth/impersonation/stop', {
      method: 'POST',
      keepalive,
      headers: {
        Authorization: `Bearer ${impersonationToken}`,
        'Content-Type': 'application/json',
      },
      // The backend takes the session from the token claim; this is informational.
      body: JSON.stringify({ sessionId }),
    });
    if (!response.ok) {
      console.warn('Failed to stop impersonation on server, proceeding with cleanup');
    }
  } catch (error) {
    console.warn('Error stopping impersonation on server:', error);
  }
};

/**
 * Refreshes the saved staff tokens (they are not refreshed while they sit in
 * originalTokens) and saves the new pair back. Returns null when the staff
 * refresh token is no longer accepted.
 */
const refreshStaffTokens = async (staff: StaffTokens): Promise<StaffTokens | null> => {
  try {
    const response = await fetch('/api/v1/auth/refresh-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: staff.refreshToken }),
    });
    if (!response.ok) return null;
    const body = await response.json().catch(() => null);
    const payload = body?.data ?? body;
    if (!payload?.accessToken || !payload?.refreshToken) return null;
    const refreshed = { ...staff, accessToken: payload.accessToken, refreshToken: payload.refreshToken };
    localStorage.setItem(ORIGINAL_TOKENS_KEY, JSON.stringify(refreshed));
    return refreshed;
  } catch {
    return null;
  }
};

export const impersonationUtils = {
  // Who may impersonate is GET /cms/me's answer: see useStaffAccess.

  // The access token decides, not localStorage: a leftover
  // impersonationSession key next to a normal token is stale state, not an
  // impersonation (next-jarvis#618).
  isImpersonating: (): boolean => {
    return readAccessTokenClaims()?.impersonated === true;
  },

  // Get current impersonation session info
  getImpersonationInfo: (): ImpersonationSession | null => {
    const session = localStorage.getItem(IMPERSONATION_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  },

  // Get impersonated user info
  getImpersonatedUserInfo: (): any => {
    const userInfo = localStorage.getItem(IMPERSONATED_USER_INFO_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  },

  // Store original tokens before impersonation (from both cookies and localStorage)
  storeOriginalTokens: (): void => {
    // Get tokens from cookies first (priority), fallback to localStorage
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    const nomorPpjk = localStorage.getItem('nomor_ppjk');

    if (accessToken && refreshToken) {
      localStorage.setItem(
        ORIGINAL_TOKENS_KEY,
        JSON.stringify({
          accessToken,
          refreshToken,
          nomorPpjk,
        })
      );
    }
  },

  // Start impersonation from the staff session.
  startImpersonation: async (targetUserId: number, reason?: string): Promise<void> => {
    // Get access token from cookies first (priority), fallback to localStorage
    const currentAccessToken = getAccessToken();
    const currentAuthData = localStorage.getItem('authData');

    if (!currentAccessToken) {
      throw new Error('No access token found');
    }

    // Store original tokens and auth data before starting impersonation
    impersonationUtils.storeOriginalTokens();
    if (currentAuthData) {
      localStorage.setItem(ORIGINAL_AUTH_DATA_KEY, currentAuthData);
    }

    let data: StartImpersonationResponse;
    try {
      data = await requestStart(currentAccessToken, targetUserId, reason);
    } catch (error) {
      // Nothing was started, so the originals just written describe the
      // current session, not one to restore later.
      localStorage.removeItem(ORIGINAL_TOKENS_KEY);
      localStorage.removeItem(ORIGINAL_AUTH_DATA_KEY);
      throw error;
    }

    applyStarted(data, reason);

    // Dispatch custom event to notify components of impersonation state change
    window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
  },

  /**
   * Switch from the current target to another one. The new impersonation is
   * started first, with the saved staff token, which is never written back as
   * the browser's session; only then is the old one stopped. So the open page
   * never runs with the staff token or with a revoked one, and a failed start
   * changes nothing: the user is still impersonating the previous target.
   * The caller reloads on 'switched'.
   */
  switchImpersonation: async (targetUserId: number, reason?: string): Promise<SwitchImpersonationResult> => {
    const previousToken = getAccessToken();
    // A token minted before sessions were bound to tokens (backend#1520) has
    // no session id; the backend would answer its Stop by ending every open
    // session of the staff member, the one just started included.
    const previousHasSession = Boolean(readAccessTokenClaims()?.impersonation_session_id);
    const previousSessionId = impersonationUtils.getImpersonationInfo()?.id ?? 0;
    const staffSession = readStaffSession();
    if (!previousToken || !staffSession) {
      return signOutAfterFailedRestore();
    }

    let staff = staffSession.tokens;
    let data: StartImpersonationResponse;
    try {
      data = await requestStart(staff.accessToken, targetUserId, reason);
    } catch (error) {
      // The saved staff access token may have expired while impersonating.
      if (!(error instanceof StartImpersonationError) || error.status !== 401) throw error;
      const refreshed = await refreshStaffTokens(staff);
      if (!refreshed) throw error;
      staff = refreshed;
      data = await requestStart(staff.accessToken, targetUserId, reason);
    }

    applyStarted(data, reason);
    // Revoke the previous target's tokens now that nothing uses them. Not
    // awaited: the new session is already in place, and the caller reloads.
    // (Start already closed the previous session row; a legacy token cannot
    // refresh, so it simply expires.)
    if (previousHasSession) {
      void postStop(previousToken, previousSessionId, { keepalive: true });
    }
    return 'switched';
  },

  // Stop impersonation and return to the staff session.
  stopImpersonation: async (): Promise<StopImpersonationResult> => {
    const session = impersonationUtils.getImpersonationInfo();
    const currentAccessToken = getAccessToken();

    if (!currentAccessToken) {
      throw new Error('No active impersonation session');
    }

    await postStop(currentAccessToken, session?.id ?? 0);

    const staffSession = readStaffSession();
    if (!staffSession) {
      console.error('Missing or unreadable original session data for restoration');
      return signOutAfterFailedRestore();
    }

    // IMPORTANT: Restore tokens in BOTH cookies AND localStorage
    // This ensures axios interceptor picks up the original user's token
    const { tokens, authData } = staffSession;
    storeJarvisTokens(tokens.accessToken, tokens.refreshToken);
    localStorage.setItem('authData', JSON.stringify(authData));

    // Restore original PPJK NPWP
    if (tokens.nomorPpjk) {
      localStorage.setItem('nomor_ppjk', tokens.nomorPpjk);
    } else {
      localStorage.removeItem('nomor_ppjk');
    }

    // Tokens are back; drop the target's caches, then the impersonation keys
    // (which notifies listeners).
    syncTabCachesWithToken();
    clearImpersonationState();
    return 'restored';
  },
};
